/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Link as ChakraLink,
    Text,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {convertSfccHrefToPwaHref} from '@consteed/frontend/utils/richTextConfigurations'
import Link from '@consteed/overrides/components/link'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import {NavigateNextIcon} from '@consteed/overrides/components/icons'

/**
 * contact tile
 *
 * @param {object} props
 * @param {string} - props.id - id (left|right).
 * @param {string} - props.buttonLink - Button Link.
 * @param {string} - props.buttonText - Button Text.
 * @param {string} - props.text - Text Overlay.
 * @param {image} - props.bgImage - Background-Image.
 * @param {width} - props.width - String.
 * @param {height} - props.height - String.
 * @param {textStyles} - props.textStyles - String.
 * @param {mouseEnterHandler} - props.mouseEnterHandler - Function.
 * @param {mouseLeaveHandler} - props.mouseLeaveHandler - Function.
 * @returns {React.ReactElement} - contactTile component.
 */
const ContactTile = ({
    id,
    buttonLink,
    buttonText,
    text,
    bgImage,
    width,
    height,
    textStyles,
    mouseEnterHandler,
    mouseLeaveHandler
}) => {
    const pwaButtonLink = convertSfccHrefToPwaHref(buttonLink)
    const isAbsolute = isAbsoluteURL(pwaButtonLink)
    const LinkWrapper = isAbsolute ? ChakraLink : Link
    const linkProps = isAbsolute ? {href: pwaButtonLink} : {to: pwaButtonLink}
    const handleMouseEnter = () => {
        mouseEnterHandler(id)
    }
    const handleMouseLeave = () => {
        mouseLeaveHandler()
    }
    return (
        <Flex
            alignItems={'end'}
            bgImage={bgImage?.url}
            backgroundPosition="center"
            backgroundRepeat={'no-repeat'}
            backgroundSize={'cover'}
            width={width}
            height={height}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            transition="width 0.3s ease-in-out"
        >
            <Box px={{base: '1rem', xl: '2rem'}} py={'2rem'}>
                <Text color={'white'}>
                    <RTE
                        richText={text}
                        textStyles={textStyles}
                        transition="font-size 0.3s ease-in-out, line-height 0.3s ease-in-out"
                    />
                </Text>
                <LinkWrapper {...linkProps}>
                    <Button size={'md'} rightIcon={<NavigateNextIcon />} mt={'1rem'}>
                        {buttonText}
                    </Button>
                </LinkWrapper>
            </Box>
        </Flex>
    )
}
ContactTile.propTypes = {
    id: PropTypes.string.isRequired,
    buttonLink: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    text: PropTypes.string,
    bgImage: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    width: PropTypes.array.isRequired,
    height: PropTypes.object.isRequired,
    textStyles: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    mouseEnterHandler: PropTypes.func,
    mouseLeaveHandler: PropTypes.func
}

/**
 * Two contact tiles each with a bgImage, text and button
 *
 * @param {object} props
 * @param {string} - props.buttonLink1 - Tile 1 Button Link.
 * @param {string} - props.buttonText1 - Tile 1 Button Text.
 * @param {string} - props.text1 - Tile 1 Text Overlay.
 * @param {image} - props.bgImage1 - Tile 1 Background-Image.
 * @param {string} - props.buttonLink2 - Tile 2 Button Link.
 * @param {string} - props.buttonText2 - Tile 2 Button Text.
 * @param {string} - props.text2 - Tile 2 Text Overlay.
 * @param {image} - props.bgImage2 - Tile 2 Background-Image.

 * @returns {React.ReactElement} - contactTiles component.
 */
export const ContactTiles = ({
    buttonLink1,
    buttonText1,
    text1,
    bgImage1,
    buttonLink2,
    buttonText2,
    text2,
    bgImage2
}) => {
    const defaultTileSizes = {left: '50%', right: '50%'}
    const defaultTextStyles = {
        left: {base: 'bookXlarge', xl: 'contactTiles'},
        right: {base: 'bookXlarge', xl: 'contactTiles'}
    }
    const imageHeight = {base: '312px', xl: '400px'}
    const [tileSizes, setTileSizes] = React.useState(defaultTileSizes)
    const [textStyles, setTextStyles] = React.useState(defaultTextStyles)
    const onMouseEnter = (id) => {
        let tileSizes
        let textStyles
        if (id === 'left') {
            tileSizes = {left: '60%', right: '40%'}
            textStyles = {
                left: {base: 'bookXlarge', xl: 'contactTiles'},
                right: {base: 'bookXlarge', xl: 'contactTilesShrinked'}
            }
        } else {
            tileSizes = {left: '40%', right: '60%'}
            textStyles = {
                left: {base: 'bookXlarge', xl: 'contactTilesShrinked'},
                right: {base: 'bookXlarge', xl: 'contactTiles'}
            }
        }
        setTileSizes(tileSizes)
        setTextStyles(textStyles)
    }
    const onMouseLeave = () => {
        setTileSizes(defaultTileSizes)
        setTextStyles(defaultTextStyles)
    }

    return (
        <Box className={'pdcOuterBox'}>
            <Flex gap={4} flexDirection={['column', 'row']}>
                <ContactTile
                    id={'left'}
                    buttonLink={buttonLink1}
                    buttonText={buttonText1}
                    text={text1}
                    bgImage={bgImage1}
                    height={imageHeight}
                    width={['100%', tileSizes.left]}
                    mouseEnterHandler={onMouseEnter}
                    mouseLeaveHandler={onMouseLeave}
                    textStyles={textStyles.left}
                />
                <ContactTile
                    id={'right'}
                    buttonLink={buttonLink2}
                    buttonText={buttonText2}
                    text={text2}
                    bgImage={bgImage2}
                    height={imageHeight}
                    width={['100%', tileSizes.right]}
                    mouseEnterHandler={onMouseEnter}
                    mouseLeaveHandler={onMouseLeave}
                    textStyles={textStyles.right}
                />
            </Flex>
        </Box>
    )
}

ContactTiles.propTypes = {
    buttonLink1: PropTypes.string,
    buttonText1: PropTypes.string,
    text1: PropTypes.string,
    bgImage1: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    buttonLink2: PropTypes.string.isRequired,
    buttonText2: PropTypes.string.isRequired,
    text2: PropTypes.string,
    bgImage2: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired
}

export default ContactTiles
